import React, { useEffect, useMemo, useState } from 'react';

import {
  Avatar,
  Box,
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {
  type MultiValue,
  type OptionProps,
  type SingleValue,
  components,
} from 'chakra-react-select';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

import { queryClient } from '@/bootstrap/react-query';
import { IconXClose } from '@/components/Icons';
import { Select } from '@/components/Select';
import { useGetCompanyUsers } from '@/features/admin-account';
import {
  adminCandidateKeys,
  useGetCompanySharesQuery,
} from '@/features/admin-candidate';
import { useGetAccountQuery } from '@/features/auth';
import { useShareCompanyMutation } from '@/features/settings/company-logo/queries';
import { useDeleteSharedInterviewMutation } from '@/features/settings/sharing-history/queries';
import {
  RoleType,
  type ShareIndexModel,
  type UserBasicDto,
} from '@/services/api';
import { getFullname } from '@/utils/identity';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  interviewId?: string;
}

interface UserOption {
  label: string;
  value: string;
  user: UserBasicDto;
}

const CustomOption = (props: OptionProps<UserOption>) => {
  const { data } = props;

  return (
    <components.Option {...props}>
      <HStack spacing="8px" align="center" w="full" borderRadius="12px">
        <Avatar
          size="sm"
          name={getFullname(data.user.name, data.user.surname)}
          bg="purple.100"
          color="purple.600"
          fontWeight="600"
          fontSize="16px"
          w="40px"
          h="40px"
        />

        <VStack spacing="0" align="start" flex="1">
          <Text
            fontSize="16px"
            fontWeight="500"
            lineHeight="1.5"
            color="gray.900"
          >
            {data.user.name} {data.user.surname}
          </Text>
          <Text
            fontSize="14px"
            fontWeight="400"
            lineHeight="1.43"
            color="gray.600"
          >
            {data.user.email}
          </Text>
        </VStack>

        <Text
          fontSize="14px"
          fontWeight="400"
          lineHeight="1.43"
          color="gray.600"
        >
          {getRoleDisplayNameLocal(data.user.role)}
        </Text>
      </HStack>
    </components.Option>
  );
};

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  interviewId,
}) => {
  const { t } = useTranslation(['common', 'interview']);
  const toast = useToast();
  const [selectedUsers, setSelectedUsers] = useState<Array<UserBasicDto>>([]);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const currentUser = useGetAccountQuery();

  const companyUsersQuery = useGetCompanyUsers({
    enabled: isOpen,
  });
  const shareCompanyLogoMutation = useShareCompanyMutation();

  const handleClose = () => {
    onClose();
  };

  const { data: companyShares, isLoading: sharesLoading } =
    useGetCompanySharesQuery(
      {
        page: 1,
        size: 1000,
        sharedInterviewId: interviewId,
        isSystemUser: true,
      },
      {
        enabled: isOpen && !!interviewId,
      }
    );

  const existingSharedUsers: Array<
    UserBasicDto & { shareData?: ShareIndexModel }
  > = useMemo(() => {
    if (!companyShares?.data) return [];

    return companyShares.data
      .filter((share) => share.sharedPersonEmail && share.sharedPersonFullName)
      .map((share) => {
        const [firstName = '', ...lastNameParts] =
          share.sharedPersonFullName?.split(' ') ?? [];
        const lastName = lastNameParts.join(' ');

        return {
          id: share.id ?? '',
          name: firstName,
          surname: lastName,
          email: share.sharedPersonEmail ?? '',
          role: undefined,
          shareData: share,
        } as UserBasicDto & { shareData?: ShareIndexModel };
      });
  }, [companyShares?.data]);

  useEffect(() => {
    if (!isOpen) {
      // Reset when modal is closed
      setSelectedUsers([]);
      setDeletingUserId(null);
      return;
    }

    // When modal is open and data is loaded, set the existing shared users
    if (isOpen && !sharesLoading && existingSharedUsers.length > 0) {
      setSelectedUsers(existingSharedUsers);
    }
  }, [isOpen, sharesLoading, existingSharedUsers]);

  const userOptions: Array<UserOption> = useMemo(() => {
    if (!companyUsersQuery.data) return [];

    return companyUsersQuery.data
      .filter((user) => user.id !== currentUser.data?.id)
      .filter(
        (user) =>
          !selectedUsers.find(
            (selected) =>
              selected.id === user.id || selected.email === user.email
          )
      )
      .map((user) => ({
        label: `${user.name} ${user.surname} \n ${user.email}`,
        value: user.id,
        user: user,
      }));
  }, [companyUsersQuery.data, currentUser.data?.id, selectedUsers]);

  const handleAddUser = (
    newValue: SingleValue<UserOption> | MultiValue<UserOption>
  ) => {
    if (newValue && !Array.isArray(newValue)) {
      const singleValue = newValue as UserOption;
      setSelectedUsers((prev) => [...prev, singleValue.user]);
    }
  };

  const deleteSharedInterviewMutation = useDeleteSharedInterviewMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminCandidateKeys.getCompanyShares({}),
      });
    },
  });

  const handleRemoveUser = async (
    user: UserBasicDto & { shareData?: ShareIndexModel }
  ) => {
    const isExistingShare = existingSharedUsers.some(
      (existing) => existing.email === user.email
    );

    if (isExistingShare && user.shareData) {
      setDeletingUserId(user.id);

      try {
        await deleteSharedInterviewMutation.mutateAsync({
          interviewId: interviewId!,
          email: user.shareData.sharedPersonEmail!,
          shareId: user.shareData.id!,
          candidateId: user.shareData.sharedCandidateId ?? '0',
        });

        setSelectedUsers((prev) =>
          prev.filter((selected) => selected.id !== user.id)
        );

        toast({
          title: t('interview:share.userRemoved'),
          status: 'success',
          duration: 3000,
        });
      } catch (error) {
        toast({
          title: t('interview:share.shareError'),
          status: 'error',
          duration: 3000,
        });
      } finally {
        setDeletingUserId(null);
      }
    } else {
      setSelectedUsers((prev) =>
        prev.filter((selected) => selected.id !== user.id)
      );
    }
  };

  const handleShare = async () => {
    if (!interviewId) return;

    const newUsersToShare = selectedUsers.filter(
      (user) =>
        !existingSharedUsers.some((existing) => existing.email === user.email)
    );

    if (newUsersToShare.length === 0) return;

    try {
      await shareCompanyLogoMutation.mutateAsync({
        interviewId,
        body: {
          userIds: newUsersToShare.map((user) => user.id),
        },
      });

      queryClient.invalidateQueries({
        queryKey: adminCandidateKeys.getCompanyShares({}),
        exact: false,
      });

      toast({
        title: t('interview:share.shareSuccess'),
        status: 'success',
        duration: 3000,
      });

      handleClose();
    } catch (error) {
      toast({
        title: t('interview:share.shareError'),
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.300" />
      <ModalContent
        borderRadius="16px"
        maxW="440px"
        bg="white"
        border="1px solid"
        borderColor="gray.100"
        p={0}
        m={0}
      >
        <ModalHeader
          p="16px 20px"
          borderBottom="1px solid"
          borderColor="gray.100"
        >
          <HStack justify="space-between" align="center">
            <Box opacity={0} w="36px" h="36px" />
            <Text
              fontSize="20px"
              fontWeight="600"
              lineHeight="1.4"
              color="gray.900"
              textAlign="center"
            >
              {t('common:actions.share')}
            </Text>
            <ModalCloseButton
              position="static"
              w="36px"
              h="36px"
              borderRadius="18px"
              border="1px solid"
              borderColor="gray.100"
              bg="white"
              _hover={{ bg: 'gray.50' }}
              _active={{ bg: 'gray.100' }}
            />
          </HStack>
        </ModalHeader>

        <ModalBody p="0" pb="20px">
          <VStack spacing="24px" align="stretch">
            <Box px="20px" pt="16px">
              <VStack spacing="16px" align="stretch" minH={270}>
                <Select
                  placeholder={t('interview:share.addPeoplePlaceholder')}
                  options={userOptions}
                  value={null}
                  onChange={handleAddUser}
                  isLoading={companyUsersQuery.isLoading}
                  isDisabled={userOptions.length === 0}
                  size="lg"
                  menuPlacement="bottom"
                  components={{
                    Option: CustomOption,
                  }}
                  chakraStyles={{
                    menuList: (provided) => ({
                      ...provided,
                      maxHeight: '300px',
                      overflowY: 'auto',
                    }),
                    option: (provided) => ({
                      ...provided,
                      padding: 0,
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      fontSize: '14px',
                    }),
                  }}
                />

                {selectedUsers.length === 0 && !companyUsersQuery.isLoading && (
                  <Text
                    fontSize="13px"
                    fontWeight="500"
                    lineHeight="1.43"
                    color="gray.500"
                    textAlign="center"
                    my="auto"
                  >
                    {t('interview:share.noUsersSelected')}
                  </Text>
                )}

                {selectedUsers.length > 0 && (
                  <VStack spacing="12px" align="stretch">
                    <Text
                      fontSize="14px"
                      fontWeight="500"
                      lineHeight="1.43"
                      color="gray.900"
                    >
                      {t('interview:share.peopleWithAccess')}
                    </Text>

                    <VStack
                      spacing="0"
                      align="stretch"
                      maxH={200}
                      overflowY="auto"
                    >
                      {selectedUsers.map((user) => {
                        return (
                          <HStack
                            key={user.id || user.email}
                            spacing="8px"
                            align="center"
                            py="8px"
                            px="0"
                            mr={2}
                          >
                            <Avatar
                              size="sm"
                              name={getFullname(user.name, user.surname)}
                              bg="purple.100"
                              color="purple.600"
                              fontWeight="600"
                              fontSize="16px"
                              w="40px"
                              h="40px"
                            />
                            <VStack spacing="0" align="start" flex="1">
                              <Text
                                fontSize="16px"
                                fontWeight="500"
                                lineHeight="1.5"
                                color="gray.900"
                              >
                                {user.name} {user.surname}
                              </Text>
                              <Text
                                fontSize="12px"
                                fontWeight="400"
                                lineHeight="1.43"
                                color="gray.600"
                              >
                                {user.email}
                              </Text>
                            </VStack>

                            <Text
                              fontSize="13px"
                              fontWeight="400"
                              lineHeight="1.43"
                              color="gray.600"
                            >
                              {(() => {
                                if (user.role) {
                                  return getRoleDisplayNameLocal(user.role);
                                }

                                const companyUser =
                                  companyUsersQuery.data?.find(
                                    (compUser) => compUser.email === user.email
                                  );

                                if (companyUser?.role) {
                                  return getRoleDisplayNameLocal(
                                    companyUser.role
                                  );
                                }
                                return t('common:information.roles.Shared');
                              })()}
                            </Text>

                            <IconButton
                              aria-label="Remove user"
                              icon={<IconXClose fontSize={18} />}
                              size="sm"
                              variant="ghost"
                              color="gray.500"
                              _hover={{ color: 'red.500', bg: 'red.50' }}
                              onClick={() => handleRemoveUser(user)}
                              isLoading={deletingUserId === user.id}
                            />
                          </HStack>
                        );
                      })}
                    </VStack>
                  </VStack>
                )}

                {!companyUsersQuery.isLoading &&
                  !sharesLoading &&
                  userOptions.length === 0 &&
                  selectedUsers.length === 0 && (
                    <Text
                      fontSize="14px"
                      color="gray.500"
                      textAlign="center"
                      py="4"
                    >
                      {t('interview:share.noUsersAvailable')}
                    </Text>
                  )}
              </VStack>
            </Box>

            <Box px="20px">
              <Button
                w="full"
                h="48px"
                mb={5}
                bg="purple.600"
                color="white"
                fontSize="16px"
                fontWeight="600"
                borderRadius="12px"
                _hover={{ bg: 'purple.700' }}
                _active={{ bg: 'purple.800' }}
                onClick={handleShare}
                isDisabled={
                  !interviewId ||
                  selectedUsers.filter(
                    (user) =>
                      !existingSharedUsers.some(
                        (existing) => existing.email === user.email
                      )
                  ).length === 0
                }
                isLoading={shareCompanyLogoMutation.isPending}
                loadingText={t('interview:share.sharing')}
              >
                {t('common:actions.share')}
              </Button>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const getRoleDisplayNameLocal = (role?: RoleType) => {
  switch (role) {
    case RoleType.SuperAdmin:
      return t('common:information.roles.SuperAdmin');
    case RoleType.Administrator:
      return t('common:information.roles.Administrator');
    case RoleType.Expert:
      return t('common:information.roles.Expert');
    default:
      return role ?? 'User';
  }
};
